// ProtectedRoute — guards routes that require authentication and/or subscription features.
// Renders children if authorized, redirects to home if not authenticated,
// shows subscription gate if authenticated but missing required feature.

import { Navigate } from 'react-router-dom';
import { useWallet } from '../contexts/WalletContext.tsx';
import { useSubscription } from '../contexts/SubscriptionContext.tsx';
import type { ReactNode, JSX } from 'react';
import { decideRouteAccess } from '../domain/route-access.ts';
import { SignInGate } from './SignInGate.tsx';

type ProtectedRouteProps = Readonly<{
  readonly children: ReactNode;
  readonly requireAuth?: boolean;
  readonly requiredFeature?: string;
  // Replaces the subscription-based feature check (e.g. queue access also counts active employees)
  readonly featureAccess?: Readonly<{ readonly known: boolean; readonly allowed: boolean }>;
}>;

export const ProtectedRoute = ({
  children,
  requireAuth = true,
  requiredFeature,
  featureAccess,
}: ProtectedRouteProps): JSX.Element => {
  const { state: walletState, connectedWallet, authState, isReconnecting, isWalletPending, walletReady } = useWallet();
  const { hasFeature, state: subState } = useSubscription();

  const access = decideRouteAccess({
    requireAuth,
    requiredFeature,
    walletReady,
    isReconnecting,
    isWalletPending,
    hasWallet: connectedWallet !== null,
    authActive: authState === 'active',
    hasSession: connectedWallet?.sessionId != null,
    restoringSession: walletState.restoringSession,
    subscriptionKnown: featureAccess?.known
      ?? (!subState.loading && (subState.status !== null || subState.error !== null)),
    hasFeature: featureAccess?.allowed ?? (requiredFeature !== undefined && hasFeature(requiredFeature)),
  });

  switch (access) {
    case 'loading':
      return <div className="page-loading" aria-busy="true" />;
    case 'redirect':
      return <Navigate to="/" replace />;
    case 'sign_in':
      return <SignInGate />;
    case 'gate':
      return (
        <div className="protected-gate">
          <h3>Feature not available</h3>
          <p>
            This page requires an active <strong>{requiredFeature}</strong> subscription.
          </p>
        </div>
      );
    case 'allow':
      return <>{children}</>;
  }
};
